import { call, put, take } from "redux-saga/effects";
import {
  alertsSuccess,
  cfReportSuccess,
  correspondentSuccess,
  error,
  instrumentSuccess,
  instrumentSummarySuccess,
  mtmReportSuccess,
  slicesSuccess,
  summarySuccess,
  varReportSuccess,
  projectedVarReportSuccess,
  projectedVarReportError,
  varReportError,
  mtmReportError,
  cfReportError,
  slicesError,
  alertsError,
  summaryError,
  instrumentError,
  instrumentSummaryError,
  correspondentError,
} from "./actions";
import {
  getAlerts,
  getInstrument,
  getInstrumentSummary,
  getReport,
  getSlices,
  getSummary,
  getCorrespondentByInstrument,
} from "./api";
import {
  ALERTS_REQUEST,
  INSTRUMENT_REQUEST,
  REPORTS_REQUEST,
  SLICES_REQUEST,
  SUMMARY_REQUEST,
} from "./constants";
import {
  RISK_CF_REPORT_ENDPOINT,
  RISK_MTM_REPORT_ENDPOINT,
  RISK_PROJECTED_VAR_REPORT_ENDPOINT,
  RISK_VAR_REPORT_ENDPOINT,
} from "../../common";
import { ApexApiError, InstrumentParams, Slice } from "../../types";

export function* fetchCfReportUrlSaga(params: Slice, token: string): Generator {
  try {
    const response: any = yield call(
      getReport,
      RISK_CF_REPORT_ENDPOINT,
      params,
      token
    );
    yield put(cfReportSuccess(response));
  } catch (err) {
    yield put(cfReportError(err));
  }
}

export function* fetchMtmReportUrlSaga(
  params: Slice,
  token: string
): Generator {
  try {
    const response: any = yield call(
      getReport,
      RISK_MTM_REPORT_ENDPOINT,
      params,
      token
    );
    yield put(mtmReportSuccess(response));
  } catch (err) {
    yield put(mtmReportError(err));
  }
}

export function* fetchVarReportUrlSaga(
  params: Slice,
  token: string
): Generator {
  try {
    const response: any = yield call(
      getReport,
      RISK_VAR_REPORT_ENDPOINT,
      params,
      token
    );
    yield put(varReportSuccess(response));
  } catch (err) {
    yield put(varReportError(err));
  }
}

export function* fetchProjectedVarReportUrlSaga(
  params: Slice,
  token: string
): Generator {
  try {
    const response: any = yield call(
      getReport,
      RISK_PROJECTED_VAR_REPORT_ENDPOINT,
      params,
      token
    );
    yield put(projectedVarReportSuccess(response));
  } catch (err) {
    yield put(projectedVarReportError(err));
  }
}

export function* fetchSlicesSaga(params: string, token: string): Generator {
  try {
    const response: any = yield call(getSlices, params, token);
    yield put(slicesSuccess(response));
  } catch (err) {
    yield put(slicesError(err));
  }
}

export function* fetchAlertsSaga(token: string): Generator {
  try {
    const response: any = yield call(getAlerts, token);
    yield put(alertsSuccess(response));
  } catch (err) {
    yield put(alertsError(err));
  }
}

export function* fetchSummarySaga(token: string): Generator {
  try {
    const response: any = yield call(getSummary, token);
    yield put(summarySuccess(response));
  } catch (err) {
    if (err.message === "Unexpected end of JSON input") {
      const error = {
        response: {
          status: 404,
        },
      };
      yield put(summaryError(error));
    } else {
      yield put(summaryError(err));
    }
  }
}

export function* fetchInstrumentSaga(
  params: InstrumentParams,
  token: string
): Generator {
  try {
    const response: any = yield call(getInstrument, params, token);
    yield put(instrumentSuccess(response));
  } catch (err) {
    if (err.message === "Unexpected end of JSON input") {
      const error = {
        response: {
          status: 404,
        },
      };
      yield put(instrumentError(error));
    } else {
      yield put(instrumentError(err));
    }
  }
}

export function* fetchInstrumentSummarySaga(
  params: InstrumentParams,
  token: string
): Generator {
  try {
    const response: any = yield call(getInstrumentSummary, params, token);
    yield put(instrumentSummarySuccess(response));
  } catch (err) {
    yield put(instrumentSummaryError(err));
  }
}

export function* fetchCorrespondentSaga(
  params: InstrumentParams,
  token: string
): Generator {
  try {
    const response: any = yield call(
      getCorrespondentByInstrument,
      params,
      token
    );
    yield put(correspondentSuccess(response));
  } catch (err) {
    yield put(correspondentError(err));
  }
}

export function* reportUrlFlow() {
  while (true) {
    const request = yield take(REPORTS_REQUEST);
    yield call(fetchCfReportUrlSaga, request.params, request.token);
    yield call(fetchMtmReportUrlSaga, request.params, request.token);
    yield call(fetchVarReportUrlSaga, request.params, request.token);
    yield call(fetchProjectedVarReportUrlSaga, request.params, request.token);
  }
}

export function* slicesFlow() {
  while (true) {
    const request = yield take(SLICES_REQUEST);
    yield call(fetchSlicesSaga, request.params, request.token);
  }
}

export function* alertsFlow() {
  while (true) {
    const request = yield take(ALERTS_REQUEST);
    yield call(fetchAlertsSaga, request.token);
  }
}

export function* summaryFlow() {
  while (true) {
    const request = yield take(SUMMARY_REQUEST);
    yield call(fetchSummarySaga, request.token);
  }
}

export function* instrumentFlow() {
  while (true) {
    const request = yield take(INSTRUMENT_REQUEST);
    yield call(fetchInstrumentSaga, request.params, request.token);
    yield call(fetchInstrumentSummarySaga, request.params, request.token);
    yield call(fetchCorrespondentSaga, request.params, request.token);
  }
}

export default [
  reportUrlFlow(),
  slicesFlow(),
  alertsFlow(),
  summaryFlow(),
  instrumentFlow(),
];
